import { useState } from 'react';
import { ApiError, api, type ConsoleSite } from '../api/client';
import { type FirstRunProgress, firstRunComplete } from '../lib/first-run';

function StepStatus({ done, children }: { done: boolean; children: string }) {
  return (
    <span className={`first-run-status ${done ? 'is-done' : 'is-waiting'}`}>
      <span aria-hidden="true">{done ? '✓' : '○'}</span> {children}
    </span>
  );
}

export function FirstRunOnboarding({
  progress,
  updateProgress,
  site,
  inventoryCount,
  inventoryError,
  canMintToken,
  onRefresh,
}: {
  progress: FirstRunProgress;
  updateProgress: (patch: Partial<Omit<FirstRunProgress, 'version'>>) => void;
  site?: ConsoleSite;
  inventoryCount: number;
  inventoryError: Error | null;
  canMintToken: boolean;
  onRefresh: () => void;
}) {
  const [minted, setMinted] = useState<{ token: string; expiresAt: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'token' | null>(null);
  const platformUrl = window.location.origin;
  const heartbeatSeen = Boolean(site?.lastSeenAt);
  const inventorySeen = inventoryCount > 0;
  const complete = firstRunComplete(site, inventoryCount);

  async function mint() {
    if (!canMintToken) return;
    setBusy(true);
    setError(null);
    try {
      setMinted(await api.connectToken());
    } catch (caught) {
      setMinted(null);
      // Prefer the API's own reason ("site limit reached") over a bare status code.
      setError(
        caught instanceof ApiError
          ? caught.explained
            ? `Could not mint a token — ${caught.message}.`
            : `Could not mint a token (${caught.status || 'network'}).`
          : 'Could not mint a token.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function copy(kind: 'url' | 'token', value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      if (kind === 'token') updateProgress({ tokenCopied: true });
      window.setTimeout(() => setCopied((current) => (current === kind ? null : current)), 1500);
    } catch {
      setError('Clipboard access is unavailable. Select and copy the value manually.');
    }
  }

  const expiresLabel = minted
    ? new Date(minted.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <section className="first-run" aria-labelledby="first-run-title">
      <div className="first-run-intro">
        <div>
          <p className="eyebrow">First-site setup</p>
          <h3 id="first-run-title">Connect your WordPress site</h3>
          <p>
            Your safe progress is saved for this tenant in this browser and synced across tabs.
            Connect tokens are never stored.
          </p>
        </div>
        <span className="badge">{complete ? 'Ready to protect' : 'Setup in progress'}</span>
      </div>

      <ol className="first-run-steps">
        <li className={progress.connectorDownloaded ? 'is-done' : undefined}>
          <div className="first-run-step-head">
            <strong>Download the connector</strong>
            <StepStatus done={progress.connectorDownloaded}>
              {progress.connectorDownloaded ? 'Downloaded' : 'Not marked downloaded'}
            </StepStatus>
          </div>
          <p>
            The connector is the off-directory bridge between WordPress and your Guard Dog tenant.
          </p>
          <a
            className="btn-primary first-run-action"
            href="/v1/console/connector/download"
            download
            onClick={() => updateProgress({ connectorDownloaded: true })}
          >
            Download connector ZIP
          </a>
        </li>

        <li className={progress.connectorActivated ? 'is-done' : undefined}>
          <div className="first-run-step-head">
            <strong>Install and activate in wp-admin</strong>
            <StepStatus done={progress.connectorActivated}>
              {progress.connectorActivated ? 'Activated' : 'Waiting for you'}
            </StepStatus>
          </div>
          <p>
            In WordPress, choose Plugins → Add New → Upload Plugin, upload the ZIP, and activate
            Guard Dog Connector. The public Guard Dog plugin should also be active.
          </p>
          <label className="first-run-check">
            <input
              type="checkbox"
              checked={progress.connectorActivated}
              onChange={(event) => updateProgress({ connectorActivated: event.target.checked })}
            />
            I installed and activated both plugins
          </label>
        </li>

        <li className={progress.tokenCopied ? 'is-done' : undefined}>
          <div className="first-run-step-head">
            <strong>Copy the connection details</strong>
            <StepStatus done={progress.tokenCopied}>
              {progress.tokenCopied ? 'Token copied' : 'Token needed'}
            </StepStatus>
          </div>
          <p>
            Open Guard Dog → Cloud in wp-admin. Paste the platform URL and a freshly minted token
            there—never into a URL or deep link.
          </p>

          <div className="token-row">
            <span className="label">Platform URL</span>
            <code className="mono">{platformUrl}</code>
            <button
              type="button"
              className="copy-btn"
              onClick={() => void copy('url', platformUrl)}
            >
              {copied === 'url' ? 'Copied ✓' : 'Copy'}
            </button>
          </div>

          <div className="token-row">
            <span className="label">Connect token</span>
            {minted ? (
              <>
                <code className="mono token-value">{minted.token}</code>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => void copy('token', minted.token)}
                >
                  {copied === 'token' ? 'Copied ✓' : 'Copy'}
                </button>
              </>
            ) : (
              <span className="muted">Not minted in this tab</span>
            )}
          </div>

          <div className="first-run-inline-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void mint()}
              disabled={busy || !canMintToken}
            >
              {busy ? 'Minting…' : minted ? 'Mint a new token' : 'Mint connect token'}
            </button>
            {expiresLabel && <span className="muted">Valid until {expiresLabel}.</span>}
          </div>
          {progress.tokenCopied && !minted && (
            <p className="muted">
              The token was not saved after refresh. If WordPress still needs one, mint a new token.
            </p>
          )}
          {!canMintToken && (
            <p className="restriction-note">
              New-site setup is paused until the plan is active. Existing sites keep protecting
              traffic.
            </p>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
        </li>

        <li className={site ? 'is-done' : undefined}>
          <div className="first-run-step-head">
            <strong>Enroll from WordPress</strong>
            <StepStatus done={Boolean(site)}>
              {site ? 'Site enrolled' : 'Watching for site'}
            </StepStatus>
          </div>
          {site ? (
            <p>
              Guard Dog found <a href={`#/sites/${site.id}`}>{site.url}</a>. No operator approval
              was needed.
            </p>
          ) : (
            <p>
              Submit the Cloud connection form in wp-admin. This console checks for the new site
              automatically.
            </p>
          )}
        </li>

        <li className={complete ? 'is-done' : undefined}>
          <div className="first-run-step-head">
            <strong>Confirm the first sync</strong>
            <StepStatus done={complete}>
              {complete ? 'Online' : 'Checking platform state'}
            </StepStatus>
          </div>
          <div className="first-run-signals" aria-live="polite">
            <StepStatus done={heartbeatSeen}>
              {heartbeatSeen ? 'Heartbeat received' : 'Waiting for heartbeat'}
            </StepStatus>
            <StepStatus done={inventorySeen}>
              {inventorySeen
                ? `${inventoryCount} inventory items received`
                : 'Waiting for inventory'}
            </StepStatus>
          </div>
          {inventoryError && (
            <p className="error">
              Inventory status could not be checked. Automatic checks will retry.
            </p>
          )}
          <button type="button" className="link-button" onClick={onRefresh}>
            Check now
          </button>
        </li>
      </ol>

      {complete && (
        <div className="first-run-complete" role="status">
          <div>
            <strong>Your first site is talking to Guard Dog.</strong>
            <p>Heartbeat and inventory are both in. The fleet view is ready when you are.</p>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => updateProgress({ dismissed: true })}
          >
            Finish setup
          </button>
        </div>
      )}
    </section>
  );
}
